import React, { useEffect, useRef, useState, useCallback } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";


import 'bootstrap/dist/css/bootstrap.min.css';
import styles from "./chatbox.module.css";

import BlockModal from "./BlockModel";
import ReportModal from "./ReportModel";
import UnmatchModal from "./UnMatchModel";
import Loader from "../../../Utility/Loader";

import log from "../../../../utils/log";
import { useCookies } from '../../../../hooks/useCookies';
import * as messageAction from "../../../../actions/Message";
import * as profileAction from "../../../../actions/Profile";

import { useSocket } from "../../../../Context/SockerContext";
import { UserApprovalEnum } from "../../../../utils/constants";
import { getProfileImageUrl } from "../../../../utils/serverUtils";

// Utility
const formatDate = date => {
  const today = dayjs();
  const messageDate = dayjs(date);
  const diff = today.diff(messageDate, "day");
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return messageDate.format("DD/MM/YYYY");
};



export default function ChatBox({ className = "" }) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { getCookie } = useCookies();

  const userId = getCookie('userId');
  const chatContainerRef = useRef(null);

  const [loader, setLoader] = useState(false);
  const [text, setText] = useState("");

  const [messages, setMessages] = useState({});
  const [conversationId, setConversationId] = useState(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnmatchModal, setShowUnmatchModal] = useState(false);

  const [participantProfile, setParticipantProfile] = useState({
    name: "",
    image: "",
    active: 1,
    approval: 10,
    deletedAt: "",
  });

  const [chatAction, setChatAction] = useState({
    isMutualMatch: true,
    isUnmatched: false,
    isBlocked: false,
    isReported: false,
  });
  const [mobile, setMobile] = useState(false);


  useEffect(() => {
    if (window.innerWidth <= 768) {
      setMobile(true);
    }
    window.addEventListener("resize", () => {
      if (window.innerWidth <= 768) {
        setMobile(true);
      } else {
        setMobile(false);
      }
    });
  }, []);

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoader(true);
      const res = await profileAction.getBasicDetails(id);
      const data = res?.data;
      if (data) {
        setParticipantProfile({
          name: data.first_name || "",
          image: getProfileImageUrl(data),
          active: data.active,
          approval: data.approval,
          deletedAt: data.deleted_at,
        });
      }

      const userStatus = await profileAction.getUserActionStatus(id);
      if (userStatus.status) {
        setChatAction({
          isMutualMatch: userStatus.data.isMutualMatch,
          isUnmatched: userStatus.data.isUnmatched,
          isBlocked: userStatus.data.isBlocked,
          isReported: userStatus.data.isReported,
        });
      }
    } catch (error) {
      log.error("Error fetching user details:", error);
    } finally {
      setLoader(false);
    }
  }, [id]);

  const fetchConversation = useCallback(async () => {
    try {
      const res = await messageAction.getChatRoom(id);
      if (res?.status) {
        setConversationId(res.conversationId);
      }
    } catch (err) {
      log.error("Error fetching conversation:", err);
    }
  }, [id]);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await messageAction.getConversion(conversationId);
      setMessages(res || {});
    } catch (error) {
      log.error("Error fetching messages:", error);
    }
  }, [conversationId]);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
      fetchConversation();
    }
  }, [id, fetchUserDetails, fetchConversation]);

  useEffect(() => {
    loadMessages();
  }, [conversationId, loadMessages]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Join & leave room
  useEffect(() => {
    if (socket && conversationId) {
      socket.emit('join-room', conversationId);
      return () => {
        socket.emit('leave-room', conversationId);
      };
    }
  }, [socket, conversationId]);

  // Socket message listener
  useEffect(() => {
    if (!socket || !conversationId) return;

    const handleMessage = (msg) => {
      if (parseInt(msg.roomId) !== parseInt(conversationId)) return;
      if (parseInt(msg.recepientId) === parseInt(userId)) {
        setMessages((prev) => ({
          ...prev,
          [dayjs().format('YYYY-MM-DD')]: [
            ...(prev[dayjs().format('YYYY-MM-DD')] ?? []),
            {
              sender: "other",
              message: msg.message,
              time: dayjs().format('h:mm A'),
            },
          ],
        }));
      }
    };

    socket.on('receive-message', handleMessage);

    return () => {
      socket.off('receive-message', handleMessage);
    };
  }, [socket, conversationId, userId]);

  // Handle reconnect
  useEffect(() => {
    if (!socket || !conversationId) return;

    const handleReconnect = () => {
      socket.emit('join-room', conversationId);
    };

    socket.on('connect', handleReconnect);
    return () => {
      socket.off('connect', handleReconnect);
    };
  }, [socket, conversationId]);

  // Handle socket errors
  useEffect(() => {
    if (!socket) return;

    const handleError = (err) => log.error('ChatBox Socket error:', err);
    const handleDisconnect = () => log.warn('ChatBox Socket disconnected');

    socket.on('error', handleError);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('error', handleError);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !conversationId) return;

    if (location?.state?.body?.type === "request") {
      await messageAction.chatRequest({
        participantId: id,
        message: text,
        conversationId: conversationId,
      });
    } else {
      socket.emit("send-message", {
        message: text,
        roomId: conversationId,
        sentAt: Date.now(),
        type: 1,
        recepientId: id,
      });
      setMessages({
        ...messages,
        [dayjs().format("YYYY-MM-DD")]: [
          ...(messages[dayjs().format("YYYY-MM-DD")]
            ? messages[dayjs().format("YYYY-MM-DD")]
            : []),
          {
            sender: "you",
            message: text,
            time: dayjs().format("h:mm A"),
          },
        ],
      });
    }

    setText("");
  };

  const renderDropdown = () => {
    const { approval, active } = participantProfile;
    const { isUnmatched, isBlocked, isReported, isMutualMatch } = chatAction;

    if (approval !== UserApprovalEnum.APPROVED) {
      return null;
    }

    const isActionDisabled = isUnmatched || isBlocked || isReported;
    if (isActionDisabled) {
      return null; // Hide the dropdown if user unmatched, blocked, or reported
    }

    return (
      <Dropdown>
        <Dropdown.Toggle as="div" style={{ cursor: "pointer" }} id="dropdown-basic">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M17.33 6.67A1.33 1.33 0 1 1 14.66 6.67 1.33 1.33 0 0 1 17.33 6.67Z" stroke="#3A3A3A" strokeWidth="2" />
            <path d="M17.33 16a1.33 1.33 0 1 1-2.67 0 1.33 1.33 0 0 1 2.67 0Z" stroke="#3A3A3A" strokeWidth="2" />
            <path d="M17.33 25.33A1.33 1.33 0 1 1 14.66 25.33 1.33 1.33 0 0 1 17.33 25.33Z" stroke="#3A3A3A" strokeWidth="2" />
          </svg>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {isMutualMatch && !isUnmatched && (
            <Dropdown.Item
              onClick={() => setShowUnmatchModal(true)}
              style={{ borderBottom: "1px solid #e0e0e0", marginBottom: "0.5rem" }}
            >
              Unmatch
            </Dropdown.Item>
          )}
          {!isReported && (
            <Dropdown.Item
              onClick={() => setShowReportModal(true)}
              style={{ borderBottom: "1px solid #e0e0e0", marginBottom: "0.5rem" }}
            >
              Report
            </Dropdown.Item>
          )}
          {!isBlocked && (
            <Dropdown.Item onClick={() => setShowBlockModal(true)}>
              Block
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderSendMessageInput = () => {
    const { approval, active } = participantProfile;
    const { isUnmatched, isBlocked, isReported } = chatAction;

    if (approval !== UserApprovalEnum.APPROVED) {
      return (
        <div className={styles.chatStatusContainer}>
          <p className={styles.chatStatusMessage}>User not approved</p>
        </div>
      );
    }

    // if (isUnmatched) {
    //   return (
    //     <div className={styles.chatStatusContainer}>
    //       <p className={styles.chatStatusMessage}>Unmatched user</p>
    //     </div>
    //   );
    // }
    // if (isBlocked) {
    //   return (
    //     <div className={styles.chatStatusContainer}>
    //       <p className={styles.chatStatusMessage}>Blocked user</p>
    //     </div>
    //   );
    // }
    // if (isReported) {
    //   return (
    //     <div className={styles.chatStatusContainer}>
    //       <p className={styles.chatStatusMessage}>Reported user</p>
    //     </div>
    //   );
    // }

    const canSendMessage =
      approval === UserApprovalEnum.APPROVED &&
      active === 1 &&
      !isUnmatched &&
      !isBlocked &&
      !isReported;

    if (!canSendMessage) return null;

    return (
      <form className={styles.chatInput} onSubmit={handleSendMessage}>
        <input
          type="text"
          placeholder="Message..."
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <button type="submit" disabled={!text.trim()}>
          Send
        </button>
      </form>
    );
  };

  if (loader) return <Loader />;

  if (!conversationId || participantProfile.deletedAt) {
    return (
      <>
        {!mobile &&
          <div className={styles.chatPlaceholder}>
            <p>Meet your match, say hello and start something wonderful!</p>
          </div>
        }
      </>

    );
  }

  return (
    <section className={[styles.chatbox, className].join(" ")}>
      <UnmatchModal personId={id} show={showUnmatchModal} setShow={setShowUnmatchModal} />
      <ReportModal personId={id} show={showReportModal} setShow={setShowReportModal} />
      <BlockModal personId={id} show={showBlockModal} setShow={setShowBlockModal} />

      <div className={styles.chatHeader}>
        <IoArrowBack size={24} cursor="pointer" onClick={() => navigate(-1)} />
        <img
          src={participantProfile.image}
          alt="profile"
          onClick={() =>
            participantProfile.approval !== 30
              ? navigate(`/user/${participantProfile.name}/${id}`)
              : navigate(`/user/home`)
          }
        />
        <p>{participantProfile.name}</p>
        <div style={{ flexGrow: "1" }}></div>
        {renderDropdown()}
      </div>

      <div id="chat-container" className={styles.chatContainer}>
        <div className={styles.chatContainerBox} ref={chatContainerRef}>

          {Object.entries(messages).map(([date, msgs]) => (
            <React.Fragment key={date}>

              <div className={styles.date}>
                <hr />
                <p>{formatDate(date)}</p>
                <hr />
              </div>
              {msgs.map((msg, idx) => (
                <div key={idx} className={styles.message}>
                  <div className={msg.sender === "you" ? styles.you : styles.other}>
                    <p style={{ fontSize: "16px" }}>{msg.message}</p>
                    <p>{dayjs(msg.sent_at).tz(dayjs.tz.guess()).format("h:mm A")}</p>
                  </div>
                </div>
              ))}

            </React.Fragment>
          ))}
        </div>

      </div>

      {renderSendMessageInput()}
    </section>
  );
}